<script setup lang="ts">
import { GridStack, type GridStackNode, type GridItemHTMLElement } from 'gridstack';
import 'gridstack/dist/gridstack.css';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue';
import {
  detachPanelGridPanel,
  mergePanelGridGroups,
  type PanelGridConfig,
  type PanelGridGroup,
} from './panelGridLayout';

const props = defineProps<{
  config: PanelGridConfig;
  panelTitles: Record<string, string>;
  layoutEditMode?: boolean;
}>();

const emit = defineEmits<{
  moveToNewSheet: [groupId: string];
  moveToSheet: [groupId: string, sheetId: string];
  sheetHover: [sheetId: string | null];
}>();

const groups = defineModel<PanelGridGroup[]>({ required: true });

const gridContainer = useTemplateRef<HTMLElement>('gridContainer');
const newSheetZone = useTemplateRef<HTMLElement>('newSheetZone');
let gridStack: GridStack | null = null;
// Ignore change events triggered by our own collapse/merge/detach updates.
let applyingProgrammatic = false;

let draggingGroupId: string | null = null;
let sheetTargetId: string | null = null;
const dragging = ref(false);
const mergeTargetId = ref<string | null>(null);
const newSheetHover = ref(false);

function initGrid() {
  const element = gridContainer.value;
  if (!element) {
    return;
  }

  gridStack = GridStack.init(
    {
      column: props.config.columns,
      cellHeight: props.config.cellHeight,
      margin: props.config.margin,
      float: true,
      handle: '.panel-drag-handle',
      staticGrid: !props.layoutEditMode,
      alwaysShowResizeHandle: true,
    },
    element,
  );

  gridStack.on('change', onGridChange);
  gridStack.on('dragstart', onDragStart);
  gridStack.on('dragstop', onDragStop);
}

onMounted(async () => {
  await nextTick();
  initGrid();
});

onBeforeUnmount(() => {
  window.removeEventListener('mousemove', onDragMouseMove);
  if (gridStack && typeof gridStack.destroy === 'function') {
    gridStack.destroy(false);
  }
  gridStack = null;
});

watch(
  () => props.layoutEditMode,
  (editMode) => {
    if (!gridStack) {
      return;
    }
    gridStack.setStatic(!editMode);
    gridStack.enableMove(!!editMode);
    gridStack.enableResize(!!editMode);
  },
);

// Rebuild whenever the set of tiles changes (merge, detach, or switching tabsheet).
const groupSignature = computed(() => groups.value.map((group) => group.id).join('|'));
watch(groupSignature, () => {
  rebuildGrid();
});

function onGridChange(_event: Event, items: GridStackNode[]) {
  if (applyingProgrammatic || !items?.length) {
    return;
  }

  const changed = new Map<string, GridStackNode>();
  for (const node of items) {
    if (typeof node.id === 'string') {
      changed.set(node.id, node);
    }
  }

  groups.value = groups.value.map((group) => {
    const node = changed.get(group.id);
    if (!node) {
      return group;
    }

    const x = typeof node.x === 'number' ? node.x : group.x;
    const y = typeof node.y === 'number' ? node.y : group.y;
    const w = typeof node.w === 'number' ? node.w : group.w;

    // While collapsed the height is pinned; only track position, keep the expanded height.
    if (group.collapsed) {
      return { ...group, x, y, w };
    }

    const h = typeof node.h === 'number' ? node.h : group.h;
    return { ...group, x, y, w, h, expandedHeight: h };
  });
}

function onDragStart(_event: Event, element: GridItemHTMLElement) {
  draggingGroupId = element.gridstackNode?.id ?? null;
  dragging.value = true;
  mergeTargetId.value = null;
  newSheetHover.value = false;
  sheetTargetId = null;
  window.addEventListener('mousemove', onDragMouseMove);
}

function onDragMouseMove(event: MouseEvent) {
  if (!gridStack || !draggingGroupId) {
    return;
  }

  const zoneRect = newSheetZone.value?.getBoundingClientRect();
  if (
    zoneRect &&
    event.clientX >= zoneRect.left &&
    event.clientX <= zoneRect.right &&
    event.clientY >= zoneRect.top &&
    event.clientY <= zoneRect.bottom
  ) {
    newSheetHover.value = true;
    mergeTargetId.value = null;
    setSheetTarget(null);
    return;
  }
  newSheetHover.value = false;

  // Dropping onto a tabsheet tab moves the whole tile to that sheet.
  let sheetHit: string | null = null;
  for (const tab of document.querySelectorAll<HTMLElement>('[data-sheet-id]')) {
    const rect = tab.getBoundingClientRect();
    if (
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom
    ) {
      sheetHit = tab.dataset.sheetId ?? null;
      break;
    }
  }
  if (sheetHit) {
    setSheetTarget(sheetHit);
    mergeTargetId.value = null;
    return;
  }
  setSheetTarget(null);

  let found: string | null = null;
  for (const item of gridStack.getGridItems()) {
    const id = item.gridstackNode?.id;
    if (!id || id === draggingGroupId) {
      continue;
    }
    const rect = item.getBoundingClientRect();
    if (
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom
    ) {
      found = id;
      break;
    }
  }
  mergeTargetId.value = found;
}

function setSheetTarget(id: string | null) {
  if (sheetTargetId !== id) {
    sheetTargetId = id;
    emit('sheetHover', id);
  }
}

function onDragStop() {
  window.removeEventListener('mousemove', onDragMouseMove);
  const source = draggingGroupId;
  const target = mergeTargetId.value;
  const toNewSheet = newSheetHover.value;
  const toSheet = sheetTargetId;
  draggingGroupId = null;
  dragging.value = false;
  mergeTargetId.value = null;
  newSheetHover.value = false;
  setSheetTarget(null);

  if (source && toNewSheet) {
    nextTick(() => emit('moveToNewSheet', source));
    return;
  }

  if (source && toSheet) {
    nextTick(() => emit('moveToSheet', source, toSheet));
    return;
  }

  if (source && target && source !== target) {
    // Defer so the grid is not rebuilt inside its own drag event.
    nextTick(() => applyMerge(source, target));
  }
}

function applyMerge(sourceId: string, targetId: string) {
  // The group-signature watcher rebuilds the grid once the model changes.
  groups.value = mergePanelGridGroups(groups.value, sourceId, targetId);
}

function detachPanel(groupId: string, panelId: string) {
  groups.value = detachPanelGridPanel(props.config, groups.value, groupId, panelId);
}

function activatePanel(groupId: string, panelId: string) {
  groups.value = groups.value.map((group) =>
    group.id === groupId ? { ...group, activePanelId: panelId } : group,
  );
}

// Rebuild the grid after structural changes so it re-reads the tiles from the model.
async function rebuildGrid() {
  applyingProgrammatic = true;
  if (gridStack && typeof gridStack.destroy === 'function') {
    gridStack.destroy(false);
  }
  gridStack = null;
  await nextTick();
  initGrid();
  await nextTick();
  applyingProgrammatic = false;
}

function toggleCollapse(groupId: string) {
  const current = groups.value.find((group) => group.id === groupId);
  if (!current) {
    return;
  }

  const element = gridStack?.getGridItems().find((item) => item.gridstackNode?.id === groupId);
  const collapsing = !current.collapsed;
  const liveHeight = element?.gridstackNode?.h;
  const expandedHeight =
    collapsing && typeof liveHeight === 'number' && liveHeight > props.config.collapsedRows
      ? liveHeight
      : current.expandedHeight;
  const nextHeight = collapsing ? props.config.collapsedRows : expandedHeight;

  groups.value = groups.value.map((group) =>
    group.id === groupId ? { ...group, collapsed: collapsing, h: nextHeight, expandedHeight } : group,
  );

  if (gridStack && element) {
    applyingProgrammatic = true;
    gridStack.update(element, { h: nextHeight });
    nextTick(() => {
      applyingProgrammatic = false;
    });
  }
}
</script>

<template>
  <div class="panel-grid-scroll">
    <div
      v-show="layoutEditMode && dragging"
      ref="newSheetZone"
      class="new-sheet-zone"
      :class="{ 'is-active': newSheetHover }"
    >
      <i class="pi pi-clone"></i>
      <span>New tabsheet</span>
    </div>
    <div ref="gridContainer" class="grid-stack panel-grid">
      <div
        v-for="group in groups"
        :key="group.id"
        class="grid-stack-item"
        :gs-id="group.id"
        :gs-x="group.x"
        :gs-y="group.y"
        :gs-w="group.w"
        :gs-h="group.h"
      >
        <div class="grid-stack-item-content panel-grid-panel" :class="{ 'is-merge-target': mergeTargetId === group.id }">
          <div class="panel-header">
            <span class="panel-drag-handle" :class="{ 'is-static': !layoutEditMode }">
              <span v-if="group.panels.length > 1" class="panel-tabs">
                <button
                  v-for="panelId in group.panels"
                  :key="panelId"
                  type="button"
                  class="panel-tab"
                  :class="{ 'is-active': panelId === group.activePanelId }"
                  @click.stop="activatePanel(group.id, panelId)"
                >
                  <span class="panel-tab-label">{{ panelTitles[panelId] }}</span>
                  <i
                    v-if="layoutEditMode"
                    class="pi pi-external-link panel-tab-detach"
                    title="Detach to its own tile"
                    @click.stop="detachPanel(group.id, panelId)"
                    @mousedown.stop
                  ></i>
                </button>
              </span>
              <span v-else class="panel-title">{{ panelTitles[group.activePanelId] }}</span>
            </span>
            <span class="panel-header-actions">
              <slot :name="`actions-${group.activePanelId}`"></slot>
            </span>
            <button
              type="button"
              class="panel-collapse-button"
              :aria-label="group.collapsed ? 'Expand panel' : 'Collapse panel'"
              @click="toggleCollapse(group.id)"
            >
              <i :class="group.collapsed ? 'pi pi-chevron-down' : 'pi pi-chevron-up'"></i>
            </button>
          </div>

          <div v-show="!group.collapsed" class="panel-body">
            <slot :name="`panel-${group.activePanelId}`"></slot>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.panel-grid-scroll {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow: auto;
  background: var(--p-content-background);
}

.new-sheet-zone {
  position: absolute;
  top: 0.4rem;
  left: 0.4rem;
  z-index: 6;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.6rem;
  border: 1px dashed color-mix(in srgb, var(--p-primary-color) 55%, var(--p-content-border-color));
  border-radius: 0.4rem;
  background: color-mix(in srgb, var(--p-content-background) 82%, var(--p-primary-color) 18%);
  color: var(--p-text-color);
  font-size: 0.78rem;
  pointer-events: none;
}

.new-sheet-zone.is-active {
  border-style: solid;
  border-color: var(--p-primary-color);
  background: color-mix(in srgb, var(--p-content-background) 65%, var(--p-primary-color) 35%);
}

.panel-grid {
  width: 100%;
  min-height: 100%;
}

.grid-stack-item-content.panel-grid-panel {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--p-content-border-color);
  border-radius: 0.4rem;
  background: var(--p-content-background);
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  min-height: 2rem;
  padding: 0.25rem 0.5rem;
  border-bottom: 1px solid var(--p-content-border-color);
}

.panel-drag-handle {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex: 1;
  min-width: 0;
  cursor: move;
  user-select: none;
  color: var(--p-text-color);
}

.panel-drag-handle.is-static {
  cursor: default;
}

.panel-header-actions {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
}

.panel-drag-handle .pi-bars {
  font-size: 0.8rem;
  opacity: 0.7;
}

.panel-title {
  font-size: 0.9rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.panel-tabs {
  display: flex;
  align-items: center;
  gap: 0.2rem;
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.panel-tab {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  max-width: 12rem;
  padding: 0.15rem 0.45rem;
  border: 1px solid transparent;
  border-radius: 4px;
  background: transparent;
  color: var(--p-text-color);
  font-size: 0.82rem;
  cursor: pointer;
}

.panel-tab-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.panel-tab.is-active {
  border-color: var(--p-content-border-color);
  background: color-mix(in srgb, var(--p-content-background) 78%, var(--p-primary-color) 22%);
  font-weight: 600;
}

.panel-tab:hover {
  border-color: var(--p-content-border-color);
}

.panel-tab-detach {
  font-size: 0.7rem;
  opacity: 0.6;
}

.panel-tab-detach:hover {
  opacity: 1;
  color: var(--p-primary-color);
}

.grid-stack-item-content.panel-grid-panel.is-merge-target {
  box-shadow: inset 0 0 0 2px var(--p-primary-color);
  border-color: var(--p-primary-color);
}

.panel-collapse-button {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.6rem;
  height: 1.6rem;
  border: 1px solid transparent;
  border-radius: 4px;
  background: transparent;
  color: var(--p-text-color);
  cursor: pointer;
}

.panel-collapse-button:hover {
  border-color: var(--p-content-border-color);
  background: color-mix(in srgb, var(--p-content-background) 80%, var(--p-primary-color) 20%);
}

.panel-body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 0.6rem;
}
</style>
